import { useState, useEffect, useCallback } from 'react';
import { Modal } from 'antd';
import { useImageBox } from '@/components/ImageBox';
import { ImageBox } from '@/components/ImageBox/ImageBox';
import { useClippoBlobs } from '@/utils';
import { LexicalEditor, $insertNodes } from 'lexical';
import { createImageNode } from '../Nodes/ImageNode';
import { useTranslation } from 'react-i18next';

export interface RichTextImageProps {
  setAttachmentIds?: (ids: string[]) => void;
  ownerId?: string;
}

interface InsertImageButtonProps extends RichTextImageProps {
  editor: LexicalEditor;
  trigger: React.ReactNode;
}

export function InsertImageButton({
  editor,
  trigger,
  setAttachmentIds,
  ownerId,
}: InsertImageButtonProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [attachmentId, setAttachmentId] = useState<string>();
  const [blobUrl, setBlobUrl] = useState<string | null>(null);

  const imageBoxProps = useImageBox({
    defer: true,
    ownerId: ownerId!,
    ownerType: 'Article',
    noPreload: true,
    reloadAfterAction: true,
  });

  const clippoBlobs = useClippoBlobs(imageBoxProps);

  useEffect(() => {
    if (clippoBlobs.blobs.length > 0) {
      const last = clippoBlobs.blobs.at(-1)!;
      setBlobUrl(last.blobURL);
      setAttachmentId(last.id);

      setAttachmentIds && setAttachmentIds(clippoBlobs.blobs.map((x) => x.id));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clippoBlobs]);

  const insert = useCallback(() => {
    if (!blobUrl || !attachmentId) return;

    editor.update(() => {
      $insertNodes([createImageNode(blobUrl, attachmentId)]);
    });

    setOpen(false);
    setBlobUrl(null);
  }, [blobUrl, editor, attachmentId]);

  return (
    <>
      <span
        onClick={() => {
          setOpen(true);
        }}
      >
        {trigger}
      </span>

      <Modal
        title={t('editor.insert.image')}
        visible={open}
        onCancel={() => setOpen(false)}
        onOk={insert}
        okButtonProps={{ disabled: !blobUrl }}
      >
        <ImageBox
          {...imageBoxProps}
          items={
            imageBoxProps.items?.length ? [imageBoxProps.items.at(-1)!] : []
          }
          compact
          single
        />
      </Modal>
    </>
  );
}
